"use client";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/IntranetHeader";
import { supabase } from "../../../supabaseClient";
import { FaTimes } from "react-icons/fa";

const formatRoleplayNhsNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length !== 10) return digits || "Generated automatically";
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

export default function CreatePatient() {
  const navigate = useNavigate();

  const [patient, setPatient] = useState({
    full_name: "",
    roblox_username: "",
    sex: "Male",
    age: "",
    triage_band: "A",
    presenting_complaint: "",
    arrival_time: "",
    location: "",
    bed: "",
  });

  const triageOptions = ["A", "B", "C", "D", "E"];
  const sexOptions = ["Male", "Female", "Other"];

  const handleChange = (field, value) => {
    setPatient((prev) => ({ ...prev, [field]: value }));
  };

  // Build a timestamp using today's date and the HH:MM entered by the user
  const buildArrivalTimestamp = (time) => {
    if (!time) return null;

    const now = new Date();
    const [hours, minutes] = time.split(":");

    now.setHours(Number(hours));
    now.setMinutes(Number(minutes));
    now.setSeconds(0);
    now.setMilliseconds(0);

    return now.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!patient.full_name || !patient.roblox_username) {
      alert("Full name and Roblox username are required");
      return;
    }

    const { data, error } = await supabase
      .from("patients")
      .insert([
        {
          full_name: patient.full_name,
          roblox_username: patient.roblox_username,
          sex: patient.sex,
          age: Number(patient.age),
          triage_band: patient.triage_band,
          presenting_complaint: patient.presenting_complaint,
          arrival_time: buildArrivalTimestamp(patient.arrival_time),
          location: patient.location,
          bed: patient.bed,
        },
      ])
      .select();

    if (error) {
      console.error(error);
      alert("Error creating patient");
    } else {
      const createdPatient = data?.[0];
      alert(
        `Patient created successfully!\nRoleplay NHS number: ${formatRoleplayNhsNumber(
          createdPatient?.roleplay_nhs_number,
        )}`,
      );
      navigate("/intranet/ecamis/index");
    }
  };

  const formatPreviewTime = (time) => {
    if (!time) return "";
    return time;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <div className="w-full px-6 mt-5">
        <div className="flex justify-between items-center mb-6">
          <h1 className="nhsuk-heading-l">Create New Patient</h1>
          <FaTimes
            className="text-black cursor-pointer"
            onClick={() => navigate("/intranet/ecamis/index")}
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Left: Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="nhsuk-label block mb-1">Full Name</label>
              <input
                className="nhsuk-input w-full"
                value={patient.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
              />
            </div>

            <div>
              <label className="nhsuk-label block mb-1">Roblox Username</label>
              <input
                className="nhsuk-input w-full"
                value={patient.roblox_username}
                onChange={(e) =>
                  handleChange("roblox_username", e.target.value)
                }
              />
            </div>

            <div>
              <label className="nhsuk-label block mb-1">Sex</label>
              <select
                className="nhsuk-select w-full"
                value={patient.sex}
                onChange={(e) => handleChange("sex", e.target.value)}
              >
                {sexOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="nhsuk-label block mb-1">Age</label>
              <input
                type="number"
                className="nhsuk-input w-full"
                value={patient.age}
                onChange={(e) => handleChange("age", e.target.value)}
              />
            </div>

            <div>
              <label className="nhsuk-label block mb-1">Triage Band</label>
              <select
                className="nhsuk-select w-full"
                value={patient.triage_band}
                onChange={(e) => handleChange("triage_band", e.target.value)}
              >
                {triageOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="nhsuk-label block mb-1">
                Presenting Complaint
              </label>
              <textarea
                className="nhsuk-textarea w-full"
                rows={3}
                value={patient.presenting_complaint}
                onChange={(e) =>
                  handleChange("presenting_complaint", e.target.value)
                }
              />
            </div>

            <div>
              <label className="nhsuk-label block mb-1">
                Arrival Time (HH:MM)
              </label>
              <input
                type="time"
                className="nhsuk-input w-full"
                value={patient.arrival_time}
                onChange={(e) => handleChange("arrival_time", e.target.value)}
              />
            </div>

            <div>
              <label className="nhsuk-label block mb-1">Location</label>
              <input
                className="nhsuk-input w-full"
                value={patient.location}
                onChange={(e) => handleChange("location", e.target.value)}
              />
            </div>

            <div>
              <label className="nhsuk-label block mb-1">Bed</label>
              <input
                className="nhsuk-input w-full"
                value={patient.bed}
                onChange={(e) => handleChange("bed", e.target.value)}
              />
            </div>

            <button type="submit" className="nhsuk-button mt-2">
              Create Patient
            </button>
          </form>

          {/* Right: Preview */}
          <div className="border border-gray-300 p-4 rounded bg-gray-50">
            <h2 className="nhsuk-heading-m mb-2">Preview</h2>
            <p>
              <strong>Full Name:</strong> {patient.full_name || "—"}
            </p>
            <p>
              <strong>Roblox Username:</strong> {patient.roblox_username || "—"}
            </p>
            <p>
              <strong>Roleplay NHS Number:</strong> Generated automatically
            </p>
            <p>
              <strong>Sex:</strong> {patient.sex}
            </p>
            <p>
              <strong>Age:</strong> {patient.age || "—"}
            </p>
            <p>
              <strong>Triage Band:</strong> {patient.triage_band}
            </p>
            <p>
              <strong>Presenting Complaint:</strong>{" "}
              {patient.presenting_complaint || "—"}
            </p>
            <p>
              <strong>Arrival Time:</strong>{" "}
              {formatPreviewTime(patient.arrival_time)}
            </p>
            <p>
              <strong>Location:</strong> {patient.location || "—"}
            </p>
            <p>
              <strong>Bed:</strong> {patient.bed || "—"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
